import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";

const colorPresets = [
  { name: "School Blue", primary: "#00305E", accent: "#A62E1D" },
  { name: "Forest Green", primary: "#1B4332", accent: "#D4A373" },
  { name: "Royal Purple", primary: "#4A1E6E", accent: "#F4A261" },
  { name: "Ocean Teal", primary: "#0F4C5C", accent: "#E36414" },
  { name: "Warm Gray", primary: "#374151", accent: "#DC2626" },
  { name: "Navy & Gold", primary: "#1E3A5F", accent: "#D4AF37" },
];

export function ColorsTab({ settings, updateSetting, updateSettings, tempPrimary, setTempPrimary, tempAccent, setTempAccent }) {
  const handleColorPreset = (preset) => {
    setTempPrimary(preset.primary);
    setTempAccent(preset.accent);
    updateSettings({ primaryColor: preset.primary, accentColor: preset.accent });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Color Theme</CardTitle>
          <CardDescription>Choose your preferred color scheme</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <Label className="text-base font-medium">Quick Presets</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {colorPresets.map((preset) => (
                <button key={preset.name} onClick={() => handleColorPreset(preset)}
                  className={`p-3 border rounded-lg text-left hover:border-gray-400 transition-colors ${
                    settings.primaryColor === preset.primary && settings.accentColor === preset.accent ? 'ring-2 ring-offset-2 ring-gray-400' : ''
                  }`} data-testid={`preset-${preset.name.toLowerCase().replace(' ', '-')}`}>
                  <div className="flex gap-2 mb-2">
                    <div className="w-8 h-8 rounded" style={{ backgroundColor: preset.primary }}></div>
                    <div className="w-8 h-8 rounded" style={{ backgroundColor: preset.accent }}></div>
                  </div>
                  <span className="text-sm font-medium">{preset.name}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-4 pt-4 border-t">
            <Label className="text-base font-medium">Custom Colors</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Primary Color (Navigation, Headers)</Label>
                <div className="flex gap-2">
                  <Input type="color" value={tempPrimary} onChange={(e) => setTempPrimary(e.target.value)} className="w-16 h-10 p-1 cursor-pointer" />
                  <Input type="text" value={tempPrimary} onChange={(e) => setTempPrimary(e.target.value)} className="flex-1 font-mono" placeholder="#00305E" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Accent Color (Buttons, Highlights)</Label>
                <div className="flex gap-2">
                  <Input type="color" value={tempAccent} onChange={(e) => setTempAccent(e.target.value)} className="w-16 h-10 p-1 cursor-pointer" />
                  <Input type="text" value={tempAccent} onChange={(e) => setTempAccent(e.target.value)} className="flex-1 font-mono" placeholder="#A62E1D" />
                </div>
              </div>
            </div>
            <Button onClick={() => { updateSettings({ primaryColor: tempPrimary, accentColor: tempAccent }); }} data-testid="apply-colors">Apply Custom Colors</Button>
          </div>
          <div className="p-4 border rounded-lg">
            <Label className="text-sm font-medium mb-3 block">Color Preview</Label>
            <div className="flex flex-wrap gap-4">
              <div className="w-32 h-12 rounded flex items-center justify-center text-white font-medium" style={{ backgroundColor: tempPrimary }}>Primary</div>
              <div className="w-32 h-12 rounded flex items-center justify-center text-white font-medium" style={{ backgroundColor: tempAccent }}>Accent</div>
              <Button style={{ backgroundColor: tempPrimary }}>Primary Button</Button>
              <Button style={{ backgroundColor: tempAccent }}>Accent Button</Button>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Theme Options</CardTitle>
          <CardDescription>Additional display preferences</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="space-y-1">
              <Label className="text-base font-medium">Dark Mode</Label>
              <p className="text-sm text-muted-foreground">Switch to a dark color scheme that's easier on the eyes</p>
            </div>
            <Switch checked={settings.darkMode} onCheckedChange={(checked) => updateSetting('darkMode', checked)} data-testid="toggle-dark-mode" />
          </div>
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="space-y-1">
              <Label className="text-base font-medium">High Contrast</Label>
              <p className="text-sm text-muted-foreground">Increase contrast for better visibility and accessibility</p>
            </div>
            <Switch checked={settings.highContrast} onCheckedChange={(checked) => updateSetting('highContrast', checked)} data-testid="toggle-high-contrast" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
